import React, { useCallback, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { InputLabel } from '@picsio/ui';
import { string } from 'prop-types';

import picsioConfig from '../../../../../../config';
import { updateMasterProduct } from '../../../store/products/updateMasterProduct';
import { createOption } from '../../../store/products/createOption';
import { createOptionValue } from '../../../store/products/createOptionValue';
import { deleteOption } from '../../../store/products/deleteOption';
import { deleteOptionValue } from '../../../store/products/deleteOptionValue';
import { addKeywords } from '../../../store/products/addKeywords';
import { removeKeywords } from '../../../store/products/removeKeywords';
import { updateMasterVariant } from '../../../store/products/actions';
import { productMightHaveVariants } from '../../../store/products/helpers/productVariantsHelpers';
import {
  selectProductIsBusy, selectMasterProduct, getPublishedChannelIdsFromVariants, getPublishedListingVariantIdsForChannel,
} from '../../../store/selectors/productSelectors';
import { setCookie, LocalStorage } from '../../../shared/utils';
import localization from '../../../shared/strings';
import { showDialog } from '../../dialog';
import { getDetailsPanelVisibility } from '../../details/helpers/getDetailsPanelDefaultState';
import { showDeleteOptionModal } from '../Options/DeleteOptionModal';
import Description from '../Description';
import ProductType from '../ProductType';
import Category from '../Category';
import Options from '../Options/Options';
import Pricing from '../Pricing';
import Inventory from '../Inventory/Inventory';
import Shipping from '../Shipping';
import { navigateToVariantParent } from '../helpers/navigateToVariantParent';
import { getVariantUnitCost } from '../helpers/getVariantUnitCost';
import { INVENTORY_PRESETS, SHIPPING_PRESETS } from '../helpers/productDetailsPresets';
import DetailsBlock from '../../DetailsBlock';
import ProductName from '../ProductName';
import Brand from '../Vendor';
import Keywords from '../Keywords';
import SeoSection from '../SeoSection';
import { stripHtmlToPlainText } from '../SeoSection/helpers/stripHtmlToPlainText';
import ProductMetaFields from '../ProductMetafields';
import { useBindProductField } from '../hooks';
import VariantDetailsHeader from '../VariantDetailsHeader';
import VariantContextBlock from '../VariantContextBlock';
import ProductLayerMetaRow from '../ProductLayerMetaRow';
import ProductStatusSelect from '../ProductStatusSelect';

const l10n = localization.PRODUCT_INFO;

const DELETE_OPTION_SKIP_KEY = 'picsio.deleteOptionSkipConfirm';
const DELETE_OPTION_VALUE_SKIP_KEY = 'picsio.deleteOptionValueSkipConfirm';

function showDeleteConfirmDialog({
  title, text, storageKey, onConfirm,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION;
  if (LocalStorage.get(storageKey)) {
    onConfirm();
    return;
  }
  showDialog({
    title,
    text,
    checkbox: { value: false, label: strings.DONT_SHOW_AGAIN },
    onOk: (state) => {
      if (state.checkbox) LocalStorage.set(storageKey, true);
      onConfirm();
    },
    textBtnOk: strings.BTN_OK,
    buttonOkColor: 'danger',
  });
}

const MasterTab = ({ productId, variantId }) => {
  const dispatch = useDispatch();
  const product = useSelector((state) => selectMasterProduct(state, productId));
  const productsState = useSelector((state) => state.products);
  const isBusy = useSelector((state) => selectProductIsBusy(state, productId));
  const teamSalesChannels = useSelector((state) => state.user.team?.salesChannels || []);

  const variant = product.variants?.find(({ _id }) => variantId === _id) ?? null;
  const isParentWithVariants = productMightHaveVariants(product);
  const isSimpleView = !isParentWithVariants;

  const [panelsVisibility, setPanelsVisibility] = useState(getDetailsPanelVisibility());

  const toggleVisibility = useCallback((title) => {
    const detailsPanelVisibility = { ...panelsVisibility };
    detailsPanelVisibility[title] = !detailsPanelVisibility[title];
    setCookie('picsio.detailsPanelVisibility', JSON.stringify(detailsPanelVisibility));
    setPanelsVisibility(detailsPanelVisibility);
  }, [panelsVisibility]);

  const onUpdateMasterProduct = useCallback((field, value) => {
    if (isBusy) return;
    dispatch(updateMasterProduct({ productId, data: { [field]: value } }));
  }, [dispatch, isBusy, productId]);

  const bindProductField = useBindProductField(onUpdateMasterProduct);

  const onCreateOption = useCallback(({ name, values }) => dispatch(createOption({ productId, name, values })), [dispatch, productId]);
  const onCreateOptionValue = useCallback(({ optionName, name }) => dispatch(createOptionValue({ productId, optionName, name })), [dispatch, productId]);
  const onDeleteOption = useCallback((optionName) => {
    const optionVariants = (product.variants || []).filter(
      (masterVariant) => Object.prototype.hasOwnProperty.call(masterVariant.options || {}, optionName),
    );
    const publishedChannelIds = new Set(getPublishedChannelIdsFromVariants(optionVariants, productsState));
    const connectedPublishedChannelIds = new Set(
      Array.from(publishedChannelIds).filter((channelId) => teamSalesChannels.some((sc) => String(sc._id) === String(channelId))),
    );
    const strings = localization.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION;
    if (!connectedPublishedChannelIds.size) {
      showDeleteConfirmDialog({
        title: strings.TITLE_CONFIRM(optionName),
        text: strings.TEXT_CONFIRM,
        storageKey: DELETE_OPTION_SKIP_KEY,
        onConfirm: () => dispatch(deleteOption({ productId, optionName })),
      });
      return;
    }
    const channels = Array.from(connectedPublishedChannelIds).map((channelId) => ({
      id: channelId,
      name: teamSalesChannels.find((sc) => String(sc._id) === String(channelId))?.name || channelId,
      listingVariantIds: getPublishedListingVariantIdsForChannel(optionVariants, channelId, productsState),
    }));
    showDeleteOptionModal({
      type: 'option',
      name: optionName,
      channelsCount: connectedPublishedChannelIds.size,
      channels,
      onConfirm: ({ listingVariantIds }) => {
        dispatch(deleteOption({ productId, optionName, listingVariantIds }));
      },
    });
  }, [dispatch, product.variants, productId, teamSalesChannels, productsState]);

  const onDeleteOptionValue = useCallback((optionName, value) => {
    const valueVariants = (product.variants || []).filter(
      (masterVariant) => (masterVariant.options || {})[optionName] === value,
    );
    const publishedChannelIds = new Set(getPublishedChannelIdsFromVariants(valueVariants, productsState));
    const connectedPublishedChannelIds = new Set(
      Array.from(publishedChannelIds).filter((channelId) => teamSalesChannels.some((sc) => String(sc._id) === String(channelId))),
    );
    const strings = localization.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION;
    if (!connectedPublishedChannelIds.size) {
      showDeleteConfirmDialog({
        title: strings.TITLE_CONFIRM_VALUE(value),
        text: strings.TEXT_CONFIRM_VALUE,
        storageKey: DELETE_OPTION_VALUE_SKIP_KEY,
        onConfirm: () => dispatch(deleteOptionValue({ productId, optionName, value })),
      });
      return;
    }
    const channels = Array.from(connectedPublishedChannelIds).map((channelId) => ({
      id: channelId,
      name: teamSalesChannels.find((sc) => String(sc._id) === String(channelId))?.name || channelId,
      listingVariantIds: getPublishedListingVariantIdsForChannel(valueVariants, channelId, productsState),
    }));
    showDeleteOptionModal({
      type: 'optionValue',
      name: value,
      channelsCount: connectedPublishedChannelIds.size,
      channels,
      onConfirm: ({ listingVariantIds }) => {
        dispatch(deleteOptionValue({
          productId, optionName, value, listingVariantIds,
        }));
      },
    });
  }, [dispatch, product.variants, productId, teamSalesChannels, productsState]);
  const onAddKeyword = useCallback((keywordName) => dispatch(addKeywords({ productId, keywordName })), [dispatch, productId]);
  const onRemoveKeyword = useCallback((keywordIds) => dispatch(removeKeywords({ productId, keywordIds })), [dispatch, productId]);

  const updateVariantField = useCallback((targetVariantId, data) => {
    if (isBusy || !targetVariantId) return;
    dispatch(updateMasterVariant({ productId, variantId: targetVariantId, data }));
  }, [dispatch, isBusy, productId]);

  const onChangePriceVariant = useCallback((price) => {
    updateVariantField(variant?._id, { price });
  }, [updateVariantField, variant]);

  const onChangeCompareAtPriceVariant = useCallback((compareAtPrice) => {
    updateVariantField(variant?._id, { compareAtPrice });
  }, [updateVariantField, variant]);

  const onChangeTaxableVariant = useCallback((taxable) => {
    updateVariantField(variant?._id, { taxable });
  }, [updateVariantField, variant]);

  const onChangeUnitCostVariant = useCallback((unitCost) => {
    updateVariantField(variant?._id, { unitCost });
  }, [updateVariantField, variant]);

  const onChangeSkuVariant = useCallback((sku) => {
    updateVariantField(variant?._id, { sku });
  }, [updateVariantField, variant]);

  const onChangeBarcodeVariant = useCallback((barcode) => {
    updateVariantField(variant?._id, { barcode });
  }, [updateVariantField, variant]);

  const onChangeTrackedVariant = useCallback((tracked) => {
    updateVariantField(variant?._id, { tracked });
  }, [updateVariantField, variant]);

  // null means the user cleared the weight — forward it as the clear instead of destructuring it.
  const onChangeWeightVariant = useCallback((weight) => {
    updateVariantField(variant?._id, {
      weight: weight ? { value: weight.value, unit: weight.unit } : null,
    });
  }, [updateVariantField, variant]);

  const onChangeRequiresShippingVariant = useCallback((value) => {
    updateVariantField(variant?._id, { requiresShipping: value });
  }, [updateVariantField, variant]);

  const onChangeCountryCodeOfOriginVariant = useCallback((countryCodeOfOrigin) => {
    updateVariantField(variant?._id, { countryCodeOfOrigin });
  }, [updateVariantField, variant]);

  const onChangeHarmonizedSystemCodeVariant = useCallback((harmonizedSystemCode) => {
    updateVariantField(variant?._id, { harmonizedSystemCode });
  }, [updateVariantField, variant]);

  const onChangeProductCost = useCallback((cost) => {
    onUpdateMasterProduct('cost', cost);
  }, [onUpdateMasterProduct]);

  const onNavigateToMasterParent = useCallback(() => {
    navigateToVariantParent({ productId });
  }, [productId]);

  const onChangeStatus = bindProductField('status');
  const masterStatusControl = (
    <ProductStatusSelect
      includeArchived
      productStatus={product.status}
      onChange={onChangeStatus}
    />
  );

  return (
    <>
      <If condition={!variant}>
        <ProductLayerMetaRow
          updatedAt={product?.updatedAt}
          timeKind="updated"
          statusLabel={l10n.masterStatusLabel}
          statusTooltip={l10n.masterStatusTooltip}
          isMasterStatus
          statusControl={masterStatusControl}
        >
        <DetailsBlock
          dataQa="productCore"
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productCoreVisibility"
          blockTitle="Core"
        >
          <ProductName value={product?.title || ''} onChange={bindProductField('title')} />
          <Brand value={product?.vendor || ''} onChange={bindProductField('vendor')} />
          <ProductType value={product?.productType || ''} onChange={bindProductField('productType')} />
          <If condition={picsioConfig.isPim}>
            <Category
              value={product?.category || null}
              disabled={isBusy}
              onChange={bindProductField('category')}
            />
          </If>
          <Description
            title="Description"
            currentValue={product?.descriptionHtml || ''}
            onChange={bindProductField('descriptionHtml')}
          />
          <br />
          <InputLabel position="top" margin>
            Keywords
          </InputLabel>
          <Keywords
            productId={productId}
            keywords={product?.keywords || []}
            onAddKeyword={onAddKeyword}
            onRemoveKeyword={onRemoveKeyword}
          />
        </DetailsBlock>
        </ProductLayerMetaRow>
        <br />
        <SeoSection
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productSeoVisibility"
          blockTitle={l10n.seo.sectionTitle}
          handle={product?.handle || ''}
          seoTitle={product?.seoTitle || ''}
          seoDescription={product?.seoDescription || ''}
          fallbackTitle={product?.title || ''}
          fallbackDescription={stripHtmlToPlainText(product?.descriptionHtml || product?.description || '')}
          onChangeHandle={bindProductField('handle')}
          onChangeSeoTitle={bindProductField('seoTitle')}
          onChangeSeoDescription={bindProductField('seoDescription')}
        />
        <br />
        <Options
          productId={productId}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productOptionsVisibility"
          blockTitle={l10n.variantStructure}
          currentValue={product?.availableOptions || []}
          onCreateOption={onCreateOption}
          onCreateOptionValue={onCreateOptionValue}
          onDeleteOption={onDeleteOption}
          onDeleteOptionValue={onDeleteOptionValue}
        />
        <If condition={isParentWithVariants || isSimpleView}>
          <br />
          <Pricing
            price={product?.price ?? ''}
            compareAtPrice={product?.compareAtPrice ?? ''}
            taxable={product?.taxable || false}
            unitCost={getVariantUnitCost(product)}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productPricingVisibility"
            blockTitle="Pricing"
            onChangePrice={bindProductField('price')}
            onChangeCompareAtPrice={bindProductField('compareAtPrice')}
            onChangeTaxable={bindProductField('taxable')}
            onChangeUnitCost={onChangeProductCost}
          />
          <br />
          <Inventory
            {...INVENTORY_PRESETS.skuCodes}
            hideTracked={false}
            tracked={product?.tracked || false}
            sku={product?.sku || ''}
            barcode={product?.barcode || ''}
            skuLabel="SKU"
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productInventoryVisibility"
            blockTitle={l10n.skuCodesBlockTitle}
            onChangeSku={bindProductField('sku')}
            onChangeBarcode={bindProductField('barcode')}
            onChangeTracked={bindProductField('tracked')}
          />
          <br />
          <Shipping
            {...SHIPPING_PRESETS.simple}
            currentValue={product?.weight || { value: 0, unit: 'grams' }}
            requiresShipping={product?.requiresShipping || false}
            countryCodeOfOrigin={product?.countryCodeOfOrigin || ''}
            harmonizedSystemCode={product?.harmonizedSystemCode || ''}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productShippingVisibility"
            blockTitle={l10n.shippingCompliance}
            onChangeRequiresShipping={bindProductField('requiresShipping')}
            onChangeWeight={bindProductField('weight')}
            onChangeCountryCodeOfOrigin={bindProductField('countryCodeOfOrigin')}
            onChangeHarmonizedSystemCode={bindProductField('harmonizedSystemCode')}
          />
        </If>
        <br />
        <If condition={picsioConfig.isPim}>
          <ProductMetaFields
            productId={productId}
            meta={product?.meta}
            layer="master"
            attributeScope="product"
            isBusy={isBusy}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productMetaVisibility"
          />
          <br />
        </If>
      </If>

      <If condition={variant}>
        <VariantDetailsHeader
          productTitle={product?.title || ''}
          variantDisplayName={variant?.displayName || ''}
          availableOptions={product?.availableOptions || []}
          variantOptions={variant?.options || {}}
        />
        <VariantContextBlock
          parentTitle={product?.title || ''}
          parentSku={product?.sku || ''}
          parentStatus={product?.status}
          availableOptions={product?.availableOptions || []}
          variantOptions={variant?.options || {}}
          onNavigateToParent={onNavigateToMasterParent}
        />
        <br />
        <Pricing
          price={variant?.price ?? ''}
          compareAtPrice={variant?.compareAtPrice ?? ''}
          taxable={variant?.taxable || false}
          unitCost={getVariantUnitCost(variant)}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productPricingVisibility"
          blockTitle="Pricing"
          onChangePrice={onChangePriceVariant}
          onChangeCompareAtPrice={onChangeCompareAtPriceVariant}
          onChangeTaxable={onChangeTaxableVariant}
          onChangeUnitCost={onChangeUnitCostVariant}
        />
        <br />
        <Inventory
          {...INVENTORY_PRESETS.skuCodes}
          hideTracked={false}
          tracked={variant?.tracked || false}
          sku={variant?.sku || ''}
          barcode={variant?.barcode || ''}
          skuLabel={l10n.variantSku}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productInventoryVisibility"
          blockTitle={l10n.skuCodesBlockTitle}
          onChangeSku={onChangeSkuVariant}
          onChangeBarcode={onChangeBarcodeVariant}
          onChangeTracked={onChangeTrackedVariant}
        />
        <br />
        <Shipping
          {...SHIPPING_PRESETS.variant}
          currentValue={variant?.weight || { value: 0, unit: 'grams' }}
          requiresShipping={variant?.requiresShipping || false}
          countryCodeOfOrigin={variant?.countryCodeOfOrigin || ''}
          harmonizedSystemCode={variant?.harmonizedSystemCode || ''}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productShippingVisibility"
          blockTitle="Shipping"
          onChangeRequiresShipping={onChangeRequiresShippingVariant}
          onChangeWeight={onChangeWeightVariant}
          onChangeCountryCodeOfOrigin={onChangeCountryCodeOfOriginVariant}
          onChangeHarmonizedSystemCode={onChangeHarmonizedSystemCodeVariant}
        />
        <br />
        <If condition={picsioConfig.isPim}>
          <ProductMetaFields
            productId={productId}
            variantId={variantId}
            meta={variant?.meta}
            layer="master"
            attributeScope="variant"
            isBusy={isBusy}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="variantMetaVisibility"
          />
          <br />
        </If>
      </If>
    </>
  );
};

MasterTab.propTypes = {
  productId: string.isRequired,
  variantId: string,
};

MasterTab.defaultProps = {
  variantId: null,
};

export default MasterTab;
